import { findPhone } from '@/utils/phoneChannels';

import { company, type PhoneId } from './company';

/**
 * Which number a WhatsApp control opens, page by page.
 *
 * Every WhatsApp button, icon and floating action on the site used to read one
 * number straight off `company.whatsappE164`, so the whole site funnelled into
 * a single handset. Different staff are strong on different things — taxes,
 * clearing, parts, registration — and a visitor on /tax-calculator is better
 * served by the person who answers tax questions all day than by whoever picks
 * up the general line.
 *
 * This file is the whole decision. It has two halves and no logic:
 *
 *   `whatsappContacts` — who exists, and their number.
 *   `whatsappRouting`  — which of them a path belongs to.
 *
 * Adding a number means typing it into `e164` below. Moving a page to someone
 * else means changing one id in the route map. No component changes either
 * way: resolution lives in `utils/whatsappContact.ts` and reaches the buttons
 * through `hooks/useWhatsAppContact.ts`.
 *
 * Nothing here is rendered. The routing is invisible by design (decided
 * 2026-10-17): the buttons look exactly as they always have, the `name` and
 * `role` fields exist to tell whoever edits this file whose number they are
 * looking at, and `/contact` and `/reviews` keep printing the main business
 * number because that is the number those two pages publish as the company's
 * public line. They are mapped to `main` below so the number a visitor reads
 * there is the number the button opens.
 */

export type WhatsAppContact = {
  /**
   * Who answers. Never rendered — it names the entry for whoever edits this
   * file, so a number is never a bare string of digits nobody can place.
   */
  name: string;
  /** What they are strong on. Never rendered; same purpose as `name`. */
  role: string;
  /**
   * The number in E.164, as `company.phones` stores them: `+256780905191`.
   *
   * `null` means the seat exists but nobody has been assigned to it yet, and
   * the control falls back to `main` rather than disappearing. That is what
   * makes this file safe to ship half-filled: until a number is typed in, the
   * routing is inert and every page behaves exactly as it did before.
   */
  e164: string | null;
};

/**
 * The seats, as the business actually divides the work (§59's services,
 * collapsed to the aspects staff specialise in).
 *
 * Fill in the numbers you have; leave the rest `null`. Ids are referenced by
 * the route map below and are checked by the compiler, so a typo in either
 * half is a build error rather than a silently dead link.
 */
const contacts = {
  /**
   * The business line, and the floor under everything else: any page with no
   * entry in the route map, and any seat whose own `e164` is still `null`,
   * opens this number.
   *
   * It is read from `company.whatsappE164` rather than retyped, which keeps
   * that field's other job intact — setting it to `null` there is still the
   * site-wide kill switch that removes every WhatsApp control at once.
   */
  main: {
    name: 'Kabex Motors',
    role: 'The general business line',
    e164: company.whatsappE164,
  },

  /** Cars, quotations, the quote list and checkout. */
  sales: { name: 'TODO', role: 'Car importation and sourcing', e164: '+256741074719' },

  /** Pre-purchase questions from somebody who has not committed yet. */
  advice: { name: 'TODO', role: 'Import consultation and guidance', e164: '+256780905191' },

  /** URA valuation, duty, and both calculators. */
  taxes: { name: 'TODO', role: 'Taxes, valuation and quotations', e164: '+256771585905' },

  /** Tax finance and payment terms. */
  finance: { name: 'TODO', role: 'Tax finance and payment support', e164: '+256759822160' },

  /** Clearing, forwarding, transit and package-dependent transport. */
  clearing: { name: 'TODO', role: 'Clearing, forwarding and logistics', e164: '+256702125861' },

  /** Plates, logbook, inspection. */
  registration: { name: 'TODO', role: 'Local vehicle registration', e164: '+256702125861' },

  /** Accessories and spares. */
  parts: { name: 'TODO', role: 'Accessories and parts', e164: '+256702125861' },

  /**
   * The sourcing desk in Japan, and the one seat no path is routed to.
   *
   * Added 2026-10-24, when the Japan number stopped being offered as a call
   * (`channels` in `config/company.ts`). It is deliberately absent from
   * `whatsappRouting` below: no page's buttons should quietly start opening a
   * desk nine hours ahead of Kampala that does not handle quotations, duty or
   * clearing. It is reached only by a control that asks for it by name —
   * `contactId="japan"` — which is exactly the escape hatch
   * `useWhatsAppContact` documents and nothing had used until now.
   *
   * The number is read from the line it belongs to rather than retyped, so
   * the digits exist once on the site. If that line is ever removed from
   * `company.phones` this resolves to `null`, which the resolver treats as an
   * unassigned seat and falls back to `main` — a working button on the
   * general line, not a dead one.
   */
  japan: {
    name: 'Kabex Motors Japan',
    role: 'Sourcing desk in Japan — WhatsApp only',
    e164: findPhone('jp-sourcing')?.e164 ?? null,
  },
} as const satisfies Record<string, WhatsAppContact>;

export type WhatsAppContactId = keyof typeof contacts;

/**
 * Which seat publishes which of the lines in `company.phones`.
 *
 * Only for a control that *prints* a number as well as linking it. Those have
 * an invariant the routed buttons do not: a printed number must open the
 * number it prints, or the page is lying to the visitor. This map is how a
 * printed number finds its seat, and the seat above reads its digits back out
 * of the same `company.phones` entry — so the two cannot drift apart, in
 * either direction, whatever either file is edited to say.
 *
 * Lines that take calls are absent: there is nothing to reconcile for a number
 * that renders as `tel:`.
 */
export const phoneWhatsAppSeats: Readonly<Partial<Record<PhoneId, WhatsAppContactId>>> = {
  'jp-sourcing': 'japan',
};

export const whatsappContacts: Readonly<Record<WhatsAppContactId, WhatsAppContact>> = contacts;

/** The seat every fallback lands on. See `main` above. */
export const MAIN_WHATSAPP_CONTACT_ID: WhatsAppContactId = 'main';

/**
 * Path → seat.
 *
 * Keys are matched by path segment, longest first, so a key covers a page and
 * everything under it: `/cars` carries `/cars/toyota/premio`, `/services`
 * carries the service pages, and a deeper key overrides a shallower one —
 * which is how `/services/tax-financing` goes to `finance` while the rest of
 * `/services` goes to `sales`. A path with no key at all goes to `main`.
 *
 * `/` is deliberately absent: the home page is the general line, which is what
 * no match already means. Adding `'/': 'sales'` would work, but it would read
 * as a prefix covering the whole site when it does not.
 *
 * Dynamic segments are not written here. The map is matched against real
 * pathnames, so `/vehicles` is the key for `/vehicles/:slug`.
 */
export const whatsappRouting: Readonly<Record<string, WhatsAppContactId>> = {
  // Buying a car.
  '/cars': 'sales',
  '/vehicles': 'sales',
  '/import-a-car': 'sales',
  '/quote': 'sales',

  // Working out the cost.
  '/tax-calculator': 'taxes',
  '/landed-cost-calculator': 'taxes',

  // Deciding whether to import at all.
  '/free-import-advice': 'advice',
  '/import-guide': 'advice',
  '/how-it-works': 'advice',

  // Parts.
  '/accessories': 'parts',

  // Cars on the water and in store.
  '/destinations': 'clearing',

  /* The services hub and its pages. The hub is a menu, so it keeps the
     general sales line; each service page below names the seat that does that
     work. Longest match wins, so these override the line above.

     Renamed 2026-10-21 with the catalogue. The old paths are not kept here as
     well — they 301 at the edge (public/.htaccess) and never reach the
     router, so a key for one would be a line nothing could ever match. */
  '/services': 'sales',
  '/services/car-importation': 'sales',
  '/services/import-consultation': 'advice',
  '/services/clearing-forwarding-and-logistics': 'clearing',
  '/services/local-vehicle-registration': 'registration',
  '/services/tax-inquiries-and-quotations': 'taxes',
  '/services/tax-financing': 'finance',

  /* Stated rather than left to the fallback: both pages print the main number
     as text beside the button, so the mapping is the thing that keeps the two
     in agreement. Changing these without changing `useContactChannels` and
     `useReviewChannels` would make a page show one number and open another. */
  '/contact': 'main',
  '/reviews': 'main',
};
